// Assumes react-toastify is installed

// @ts-nocheck
import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ContentLoader, { Rect } from "react-content-loader";

import {
  addAchievementToProfile,
  fetchUserOwnerProfile,
  fetchUserPosts,
  removeAchievementFromProfile,
  updateProfilePicture,
} from "../store/postsSlice";
import { logoutUser } from "../store/userSlice";
import { BASE_URL } from "../Constants/BaseUrl";

import { formatDateMMDDYY } from "../Utils/DateFormater";
import EditProfileModal from "../components/EditProfleModal";
import ImagePicker from "../components/ImagePicker";
import { useAppSelector } from "../hooks/useRedux";
import { EyeOff, Lock, Settings, Video } from "lucide-react";

const UserProfileScreen = () => {
  const color = "text-white bg-gray-900";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const userId = user?.user_id?.toString();
  const userDetails = useAppSelector((state) => state.posts.ownerPublicProfile);
  const { userPosts, loading } = useAppSelector((state) => state.posts);

  const [refreshing, setRefreshing] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [profilePicLoading, setProfilePicLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [text, setText] = useState("");
  const [add, setAdd] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserOwnerProfile(userId));
      dispatch(fetchUserPosts(userId));
    }
  }, [dispatch, userId]);

  const addAchievementHandler = async () => {
    setButtonLoading(true);
    setText("");
    setAdd(false);

    const data = new FormData();
    data.append("athlete_id", user.user_id?.toString());
    data.append("achievement", text);

    try {
      const response = await fetch(`${BASE_URL}/achievement/create`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: data,
      });
      const result = await response.json();
      if (result?.data) {
        dispatch(addAchievementToProfile(result.data));
        toast.success("Achievement added successfully!");
      }
    } catch (error) {
      toast.error("Failed to add achievement. Please try again later.");
    } finally {
      setButtonLoading(false);
    }
  };

  const handleDeleteAchievement = async (achievementId) => {
    if (!achievementId) return;
    setAdd(false);
    setButtonLoading(true);

    const data = new FormData();
    data.append("id", achievementId?.toString());

    try {
      const response = await fetch(`${BASE_URL}/achievement/delete`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: data,
      });
      const result = await response.json();
      if (result?.success) {
        dispatch(removeAchievementFromProfile(achievementId));
        toast.success("Achievement deleted successfully!");
      }
    } catch (error) {
      toast.error("Failed to delete achievement. Please try again later.");
    } finally {
      setButtonLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      dispatch(fetchUserOwnerProfile(userId)),
      dispatch(fetchUserPosts(userId)),
    ]);
    setRefreshing(false);
  };

  const SkeletonLoader = () => (
    <div className="space-y-6">
      <ContentLoader
        height={120}
        width={400}
        backgroundColor="#333"
        foregroundColor="#999"
      >
        <circle cx="50" cy="60" r="40" />
        <rect x="110" y="40" rx="4" ry="4" width="200" height="10" />
        <rect x="110" y="60" rx="4" ry="4" width="140" height="10" />
      </ContentLoader>

      <ContentLoader
        height={180}
        width={400}
        backgroundColor="#333"
        foregroundColor="#999"
      >
        <rect x="0" y="10" rx="4" ry="4" width="350" height="20" />
        <rect x="0" y="40" rx="4" ry="4" width="380" height="10" />
        <rect x="0" y="60" rx="4" ry="4" width="320" height="10" />
        <rect x="0" y="80" rx="4" ry="4" width="300" height="10" />
        <rect x="0" y="100" rx="4" ry="4" width="280" height="10" />
      </ContentLoader>

      <div className="grid grid-cols-3 gap-2">
        {[...Array(6)].map((_, i) => (
          <ContentLoader
            key={i}
            height={120}
            width={120}
            backgroundColor="#333"
            foregroundColor="#999"
          >
            <rect x="0" y="0" rx="4" ry="4" width="100%" height="100%" />
          </ContentLoader>
        ))}
      </div>
    </div>
  );

  if (loading)
    return (
      <div className="min-h-screen p-4 bg-black text-white">
        <SkeletonLoader />
      </div>
    );

  return (
    <div className="min-h-screen p-4 bg-black text-white">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <img
            // src={
            //   userDetails?.profile_picture
            //     ? `${BASE_URL}/${userDetails?.profile_picture}`
            //     : "https://encrypted-tbn0.gstatic.com/images?q=trbn:ANd9GcQfj3Bm37Nn_rBQHkIzxnpmGMv3AnLWNYvA1_GwXzebfQ7rxLHl0fRsKo6mIi1SmoOiCL4&usqp=CAU"
            // }
            src={
              userDetails?.profile_picture?.startsWith("https")
                ? userDetails.profile_picture
                : userDetails?.profile_picture
                  ? `${BASE_URL}/${userDetails.profile_picture}`
                  : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQfj3Bm37Nn_rBQHkIzxnpmGMv3AnLWNYvA1_GwXzebfQ7rxLHl0fRsKo6mIi1SmoOiCL4&usqp=CAU"
            }
            alt="Profile"
            aria-label="Change profile picture"
            className="w-32 h-32 rounded-full border-2 border-white cursor-pointer hover:opacity-80 transition-opacity duration-200 group ml-4"
            onClick={() => setShowImagePicker(true)}
          />
          <div className="flex center gap-5">
            <h2 className="text-lg font-semibold">
              {userDetails?.athlete_info?.first_name}{" "}
              {userDetails?.athlete_info?.last_name}
            </h2>
            {userDetails?.profile_visibility === "private" && (
              <Lock color="#fff" />
            )}
            {/* <div className="flex gap-4 mt-2">
              <span>0 Followers</span>
              <span>0 Following</span>
            </div> */}
          </div>
        </div>
        {/* <button
          className="p-2 bg-black text-white rounded hover:bg-gray-600"
          onClick={() => setShowSettings(true)}
        >
          <Settings className="w-8 h-8" />
        </button> */}
        <div className="flex items-center gap-2">
          <button
            className="p-2 bg-black text-white rounded hover:bg-gray-600"
            onClick={() => setShowSettings(true)}
          >
            <Settings className="w-8 h-8" />
          </button>
          {/* <button
            className="p-2 bg-black text-white rounded hover:bg-gray-600"
            onClick={() =>
              navigate("/live-stream", {
                state: {
                  isBroadcaster: true,
                  channelName: `live_${userId}`,
                },
              })
            }
          >
            <Video className="w-8 h-8" />
          </button> */}
        </div>
      </div>

      {/* Profile info block */}
      <div className="grid grid-cols-2 gap-4 text-sm text-white ml-5">
        <div>
          {/* <p>
            <strong>Date of Birth:</strong>{" "}
            {formatDateMMDDYY(userDetails?.athlete_info?.dob) || "Not Found"}
          </p> */}
          {/* <p>
            <strong>Gender:</strong>{" "}
            {userDetails?.athlete_info?.gender || "Not Found"}
          </p> */}
          <p>
            <strong>Weight:</strong>{" "}
            {userDetails?.athlete_info?.weight
              ? `${userDetails.athlete_info.weight} lbs`
              : "Not Found"}
          </p>
          <p>
            <strong>Height:</strong>{" "}
            {userDetails?.athlete_info?.height || "Not Found"}
          </p>
          <p>
            <strong>Division:</strong>{" "}
            {userDetails?.athlete_info?.division || "Not Found"}
          </p>
          <p>
            <strong>School:</strong>{" "}
            {userDetails?.athlete_info?.current_school || "Not Found"}
          </p>
        </div>
        <div>
          {/* <p>
            <strong>School:</strong>{" "}
            {userDetails?.athlete_info?.current_school || "Not Found"}
          </p> */}
          <p>
            <strong>Sport:</strong>{" "}
            {userDetails?.athlete_info?.sport_name || "Not Found"}
          </p>
          <p>
            <strong>Position:</strong>{" "}
            {userDetails?.athlete_info?.position || "Not Found"}
          </p>
          <p>
            <strong>School Year:</strong>{" "}
            {userDetails?.athlete_info?.school_year || "Not Found"}
          </p>
        </div>
      </div>

      {/* Achievements */}
      <div className="p-4 rounded mb-6">
        <div className="flex justify-between items-center mb-0">
          <h3 className="text-white font-semibold text-sm">STATISTICS</h3>
          {!add && (
            <button
              onClick={() => setAdd(true)}
              className="w-6 h-6 bg-red-600 text-white flex items-center justify-center rounded pb-1 cursor-pointer"
            >
              +
            </button>
          )}
        </div>
        {add && (
          <div className="mb-3">
            <input
              type="text"
              maxLength={100}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onBlur={() => setAdd(false)}
              onKeyDown={(e) => e.key === "Enter" && addAchievementHandler()}
              className="w-full p-2 rounded bg-gray-900 text-white border border-gray-600"
              placeholder="🏆 Write your achievement"
            />
            <p className="text-xs text-gray-400 text-right mt-1">
              {100 - text.length} characters left
            </p>
          </div>
        )}
        {userDetails?.athlete_achievements?.map((item, index) => (
          <div
            key={index}
            className="flex justify-between items-center border-b border-gray-700 py-2"
          >
            <p className="text-white text-sm flex-1 mr-2">{item.achievement}</p>
            <button
              className="text-red-400 hover:text-red-600"
              onClick={() => handleDeleteAchievement(item.id)}
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      <div>
        {userPosts?.length > 0 ? (
          <div className="grid grid-cols-3 gap-2 mt-10">
            {userPosts.map((item) => {
              const imageUrl = item.image_url;
              const videoUrl = item.video_url;
              const likesCount = item.like_count || 0;

              const isVideo = Boolean(videoUrl);
              const isImage = Boolean(imageUrl);

              return (
                <div
                  key={item.post_id}
                  className="relative aspect-square overflow-hidden rounded-md cursor-pointer"
                  onClick={() =>
                    navigate("/post-preview", {
                      state: {
                        currentPost: item,
                        posts: userPosts,
                      },
                    })
                  }
                >
                  {isImage ? (
                    <img
                      // src={`${BASE_URL}${imageUrl}`}
                      src={
                        imageUrl?.startsWith("https")
                          ? imageUrl
                          : imageUrl
                            ? `${BASE_URL}/${imageUrl}`
                            : undefined
                      }
                      alt="post"
                      className="w-full h-full object-cover"
                    />
                  ) : isVideo ? (
                    <video
                      // src={`${BASE_URL}${videoUrl}`}
                      src={
                        videoUrl?.startsWith("https")
                          ? videoUrl
                          : videoUrl
                            ? `${BASE_URL}/${videoUrl}`
                            : undefined
                      }
                      className="w-full h-full object-cover"
                      muted
                      loop
                      playsInline
                    />
                  ) : null}

                  <div className="absolute bottom-1 left-1 flex items-center gap-1 text-white text-xs">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4 text-red-500"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 3.75a5.25 5.25 0 00-4.5 2.4 5.25 5.25 0 00-9 3.6c0 5.25 9 11.25 9 11.25s9-6 9-11.25a5.25 5.25 0 00-4.5-5.4z"
                      />
                    </svg>
                    {likesCount}
                  </div>

                  <div className="absolute bottom-1 right-1 text-white text-xs">
                    {isImage && <span>📷</span>}
                    {isVideo && <span>🎥</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex mt-10 ml-5 justify-center">
            <p className="text-gray-400 text-lg">No posts yet</p>
          </div>
        )}
      </div>

      <EditProfileModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        userDetails={userDetails}
      />

      <ImagePicker
        show={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onImageSelected={(file) => {
          setProfilePicLoading(true);
          dispatch(updateProfilePicture(file))
            .unwrap()
            .then(() => {
              toast.success("Profile picture updated!");
            })
            .catch((error) => {
              toast.error(error || "Failed to update profile picture");
            })
            .finally(() => {
              setProfilePicLoading(false);
            });
        }}
        // profileImageUri={
        //   userDetails?.profile_picture
        //     ? `${BASE_URL}/${userDetails?.profile_picture}`
        //     : "https://encrypted-tbn0.gstatic.com/images?q=trbn:ANd9GcQfj3Bm37Nn_rBQHkIzxnpmGMv3AnLWNYvA1_GwXzebfQ7rxLHl0fRsKo6mIi1SmoOiCL4&usqp=CAU"
        // }
        profileImageUri={
          userDetails?.profile_picture?.startsWith("https")
            ? userDetails.profile_picture
            : userDetails?.profile_picture
              ? `${BASE_URL}/${userDetails.profile_picture}`
              : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQfj3Bm37Nn_rBQHkIzxnpmGMv3AnLWNYvA1_GwXzebfQ7rxLHl0fRsKo6mIi1SmoOiCL4&usqp=CAU"
        }
      />
    </div>
  );
};

export default UserProfileScreen;
